import { useMemo } from "react";
import * as THREE from "three";
import { BUILDING_CONFIG } from "../../config/constants";
import { getBuildingStructure } from "../../config/buildingPresets";
import { createBoxEdges, isEdgeInsideBox } from "../../utils/buildingGeometry";
import type { PlacedObject } from "../../config/types";

interface MergedBuildingEdgesProps {
  buildings: PlacedObject[];
  hoveredBuildingId?: string | null; // 호버된 건물 ID
  opacity?: number; // 투명도 (0~1, 기본값 1)
  selectedBuildingId?: string | null; // 선택된 건물 ID (선택 시 다른 건물 엣지 투명 처리)
}

// 엣지용 vertex shader (GUI 클리핑 지원)
const edgeVertexShader = `
varying vec4 vGroundClipPos;
varying vec3 vColor;

void main() {
  vColor = color;
  
  // 바닥 지점(Y=0)의 클립 좌표 계산
  vec4 groundPos = vec4(position.x, 0.0, position.z, 1.0);
  vGroundClipPos = projectionMatrix * modelViewMatrix * groundPos;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// 엣지용 fragment shader (GUI 클리핑 지원)
const edgeFragmentShader = `
uniform float opacity;

// GUI 클리핑용 uniforms
uniform float uGUIControlPoint;
uniform vec2 uWorldXDir;
uniform vec2 uWorldZDir;
uniform float uEnableGUIClipping;
uniform float uGUIClippingDebug;
// 각 코너별 enable 상태
uniform float uTopLeftEnabled;
uniform float uTopRightEnabled;
uniform float uBottomLeftEnabled;
uniform float uBottomRightEnabled;
// 각 코너별 hypotenuseOffset (애니메이션용, 1.0=모서리에, 0.0=원래위치)
uniform float uTopLeftOffset;
uniform float uTopRightOffset;
uniform float uBottomLeftOffset;
uniform float uBottomRightOffset;

varying vec4 vGroundClipPos;
varying vec3 vColor;

// 선의 어느 쪽에 점이 있는지 계산
float sideOfLine(vec2 linePoint, vec2 lineDir, vec2 point) {
  vec2 perp = vec2(-lineDir.y, lineDir.x);
  return dot(point - linePoint, perp);
}

// GUI 삼각형 영역 내부인지 체크 (hypotenuseOffset 포함)
int whichGUITriangle(vec2 ndc) {
  float controlX = uGUIControlPoint * 2.0 - 1.0;
  
  // ===== TopLeft 삼각형 =====
  vec2 topLeftCorner = vec2(-1.0, 1.0);
  vec2 baseTopLeftControl = vec2(controlX, 1.0);
  vec2 topLeftPerp = normalize(vec2(uWorldZDir.y, -uWorldZDir.x));
  float topLeftDistToCorner = length(baseTopLeftControl - topLeftCorner);
  vec2 topLeftControl = baseTopLeftControl + topLeftPerp * uTopLeftOffset * topLeftDistToCorner * 1.5;
  float sideTopLeft = sideOfLine(topLeftControl, uWorldZDir, ndc);
  bool inTopLeft = sideTopLeft < 0.0 && uTopLeftEnabled > 0.5;
  
  // ===== TopRight 삼각형 =====
  vec2 topRightCorner = vec2(1.0, 1.0);
  vec2 baseTopRightControl = vec2(controlX, 1.0);
  vec2 topRightPerp = normalize(vec2(-uWorldXDir.y, uWorldXDir.x));
  float topRightDistToCorner = length(baseTopRightControl - topRightCorner);
  vec2 topRightControl = baseTopRightControl + topRightPerp * uTopRightOffset * topRightDistToCorner * 1.5;
  float sideTopRight = sideOfLine(topRightControl, uWorldXDir, ndc);
  bool inTopRight = sideTopRight > 0.0 && uTopRightEnabled > 0.5;
  
  // ===== BottomLeft 삼각형 =====
  vec2 bottomLeftCorner = vec2(-1.0, -1.0);
  vec2 baseBottomLeftControl = vec2(controlX, -1.0);
  vec2 bottomLeftPerp = normalize(vec2(uWorldXDir.y, -uWorldXDir.x));
  float bottomLeftDistToCorner = length(baseBottomLeftControl - bottomLeftCorner);
  vec2 bottomLeftControl = baseBottomLeftControl + bottomLeftPerp * uBottomLeftOffset * bottomLeftDistToCorner * 1.5;
  float sideBottomLeft = sideOfLine(bottomLeftControl, uWorldXDir, ndc);
  bool inBottomLeft = sideBottomLeft < 0.0 && uBottomLeftEnabled > 0.5;
  
  // ===== BottomRight 삼각형 =====
  vec2 bottomRightCorner = vec2(1.0, -1.0);
  vec2 baseBottomRightControl = vec2(controlX, -1.0);
  vec2 bottomRightPerp = normalize(vec2(-uWorldZDir.y, uWorldZDir.x));
  float bottomRightDistToCorner = length(baseBottomRightControl - bottomRightCorner);
  vec2 bottomRightControl = baseBottomRightControl + bottomRightPerp * uBottomRightOffset * bottomRightDistToCorner * 1.5;
  float sideBottomRight = sideOfLine(bottomRightControl, uWorldZDir, ndc);
  bool inBottomRight = sideBottomRight > 0.0 && uBottomRightEnabled > 0.5;
  
  if (inTopLeft) return 1;
  if (inTopRight) return 2;
  if (inBottomLeft) return 3;
  if (inBottomRight) return 4;
  
  return 0;
}

void main() {
  // GUI 클리핑
  if (uEnableGUIClipping > 0.5) {
    vec2 ndc = vGroundClipPos.xy / vGroundClipPos.w;
    int tri = whichGUITriangle(ndc);
    
    if (tri > 0) {
      if (uGUIClippingDebug > 0.5) {
        // 디버그 모드: 색상 시각화
        if (tri == 1) {
          gl_FragColor = vec4(1.0, 0.0, 0.0, opacity);
        } else if (tri == 2) {
          gl_FragColor = vec4(0.0, 1.0, 0.0, opacity);
        } else if (tri == 3) {
          gl_FragColor = vec4(0.0, 0.0, 1.0, opacity);
        } else {
          gl_FragColor = vec4(1.0, 1.0, 0.0, opacity);
        }
        return;
      }
      discard;
    }
  }
  
  gl_FragColor = vec4(vColor, opacity);
}
`;

/**
 * 모든 건물의 엣지를 하나의 BufferGeometry로 병합하여 렌더링
 * Draw calls를 건물 수만큼 감소시킴
 * GUI 클리핑 지원 (커스텀 ShaderMaterial 사용)
 */
export function MergedBuildingEdges({
  buildings,
  hoveredBuildingId = null,
  opacity = 1.0,
}: MergedBuildingEdgesProps) {
  const mergedGeometry = useMemo(() => {
    if (buildings.length === 0) return null;

    const allEdgePositions: number[] = [];
    const allEdgeColors: number[] = [];

    buildings.forEach((building) => {
      const structure = building.buildingStructure?.length
        ? building.buildingStructure
        : getBuildingStructure(building.meshIndex);

      const [bx, by, bz] = building.position;
      // 호버된 건물은 Y 위치를 0.3 올림
      const hoverOffset = building.id === hoveredBuildingId ? 0.3 : 0;

      // 유저 컬러 파싱 (hex string -> RGB)
      let userR = 0;
      let userG = 0;
      let userB = 0;
      if (building.userColor) {
        const hex = building.userColor.replace("#", "");
        userR = parseInt(hex.substring(0, 2), 16) / 255;
        userG = parseInt(hex.substring(2, 4), 16) / 255;
        userB = parseInt(hex.substring(4, 6), 16) / 255;

        // 채도 낮추기: 회색과 섞기 (50% 회색, 50% 원본 색상)
        const grayMix = 0.5;
        const avg = (userR + userG + userB) / 3;
        userR = userR * (1 - grayMix) + avg * grayMix;
        userG = userG * (1 - grayMix) + avg * grayMix;
        userB = userB * (1 - grayMix) + avg * grayMix;

        // 명도 낮추기: 전체적으로 어둡게 (0.65 배율)
        const brightness = 0.65;
        userR *= brightness;
        userG *= brightness;
        userB *= brightness;
      } else {
        // 기본 엣지 컬러 사용
        const defaultColor = new THREE.Color(BUILDING_CONFIG.EDGE_COLOR);
        userR = defaultColor.r;
        userG = defaultColor.g;
        userB = defaultColor.b;
      }

      // 각 박스의 엣지 생성
      structure.forEach((box, boxIndex) => {
        const edges = createBoxEdges(box);

        edges.forEach(([p1, p2]) => {
          // 다른 박스에 가려지는 엣지는 제외
          let isHidden = false;
          for (let i = 0; i < structure.length; i++) {
            if (i === boxIndex) continue;
            if (isEdgeInsideBox(p1, p2, structure[i])) {
              isHidden = true;
              break;
            }
          }

          if (!isHidden) {
            // 건물 월드 위치 적용 (호버 시 Y 오프셋 추가)
            allEdgePositions.push(
              p1.x + bx,
              p1.y + by + hoverOffset,
              p1.z + bz,
              p2.x + bx,
              p2.y + by + hoverOffset,
              p2.z + bz
            );
            // 각 엣지의 두 점에 색상 추가
            allEdgeColors.push(userR, userG, userB, userR, userG, userB);
          }
        });
      });
    });

    if (allEdgePositions.length === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(allEdgePositions, 3)
    );
    geometry.setAttribute(
      "color",
      new THREE.Float32BufferAttribute(allEdgeColors, 3)
    );

    return geometry;
  }, [buildings, hoveredBuildingId]);

  // 커스텀 ShaderMaterial (GUI 클리핑 지원)
  const material = useMemo(() => {
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        opacity: { value: opacity },
        // GUI 클리핑용 uniforms (GUIClippingManager에서 업데이트됨)
        uViewProjectionMatrix: { value: new THREE.Matrix4() },
        uGUIControlPoint: { value: 0.5 },
        uWorldXDir: { value: new THREE.Vector2(0.866, -0.5) },
        uWorldZDir: { value: new THREE.Vector2(-0.866, -0.5) },
        uEnableGUIClipping: { value: 0.0 },
        uGUIClippingDebug: { value: 0.0 },
        // 각 코너별 enabled (기본값: 모두 활성화)
        uTopLeftEnabled: { value: 1.0 },
        uTopRightEnabled: { value: 1.0 },
        uBottomLeftEnabled: { value: 1.0 },
        uBottomRightEnabled: { value: 1.0 },
        // 각 코너별 hypotenuseOffset (기본값: 0.0 = 원래 위치)
        uTopLeftOffset: { value: 0.0 },
        uTopRightOffset: { value: 0.0 },
        uBottomLeftOffset: { value: 0.0 },
        uBottomRightOffset: { value: 0.0 },
      },
      vertexShader: edgeVertexShader,
      fragmentShader: edgeFragmentShader,
      vertexColors: true,
      // 항상 transparent로 설정하여 GUI Triangle과 같은 pass에서 renderOrder로 정렬
      transparent: true,
    });
    // Stencil test 무시 (shader에서 GUI clipping 처리)
    mat.stencilWrite = false;
    mat.stencilFunc = THREE.AlwaysStencilFunc;
    return mat;
  }, [opacity]);

  if (!mergedGeometry) return null;

  return <lineSegments geometry={mergedGeometry} material={material} />;
}
